import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { FormatConfigService } from './format-config.service';
import { AppConfigService } from './app-config.service';
import {
  BaseerweiterungService,
  BaseformatService,
  BaseimageService,
  BaseorderService,
  BaseparameterService,
  BasesettingService,
  ErweiterungConfigResponse,
  FormatConfigResponse,
  ImageConfigResponse,
  MenuConfigResponse,
  ParameterConfigResponse,
  SettingsConfigResponse
} from 'app/core/base-api';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { AppPrintConfig, CookiePrintConfig } from './app-print-config';
import { AppFormatConfig } from './app-format-config';
import { AppImageConfig } from './app-image-config';
import { AppAuszugCookieService } from './app-auszug-cookie-service';
import { environment } from 'environments/environment';
import { GbprintService, PrintConfigResponseMeta } from 'app/core/gb-api';
import { filter, first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AppFormatConfigService extends FormatConfigService {
  private appConfigService = inject(AppConfigService);
  private imageService = inject(BaseimageService);
  private settingService = inject(BasesettingService);
  private formatService = inject(BaseformatService);
  private menuService = inject(BaseorderService);
  private translateService = inject(TranslateService);
  private erweiterungService = inject(BaseerweiterungService);
  private parameterService = inject(BaseparameterService);
  private gbPrintService = inject(GbprintService);
  private appAuszugCookieService = inject(AppAuszugCookieService);

  private _formatConfig = new BehaviorSubject<AppFormatConfig>(null);
  private _printConfig = new BehaviorSubject<AppPrintConfig>(null);
  private _settingsConfig = new BehaviorSubject<SettingsConfigResponse>(null);
  private _erweiterungConfig = new BehaviorSubject<ErweiterungConfigResponse>(null);
  private _parameterConfig = new BehaviorSubject<ParameterConfigResponse>(null);

  private _menuConfigSubject = new BehaviorSubject<MenuConfigResponse>(null);
  private _menuConfig$: Observable<MenuConfigResponse> = this._menuConfigSubject.asObservable();

  private _imageConfigSubject = new BehaviorSubject<AppImageConfig>(null);
  private _imageConfig$: Observable<AppImageConfig> = this._imageConfigSubject.asObservable();

  private _initializedSubject = new BehaviorSubject<boolean>(false);
  private _initialized$: Observable<boolean> = this._initializedSubject.asObservable();

  private _deploymentMode = environment.deploymentMode;

  // @ts-ignore
  init(): Promise<any> {
    inject(OAuthService).events
      .pipe(
        filter(data => data.type === 'token_received'),
        first()
      )
      .subscribe(() => {
        this.configsLoaded(() => undefined);
      });
    return new Promise<any>((resolve) => {
      this.configsLoaded(resolve);
    });
  }

  configsLoaded(resolve) {
    const imageConfig$ = this.imageService.getImageConfig(this.appConfigService.kanton);
    const formatConfig$ = this.formatService.getFormatConfig(this.appConfigService.kanton);
    const settingsConfig$ = this.settingService.getSettingsConfig();
    const menuConfig$ = this.menuService.getMenuConfig(this.appConfigService.kanton, this.getDeploymentMode());
    const erweiterungConfig$ = this.erweiterungService.getActiveErweiterungen();
    const parameterConfig$ = this.parameterService.getAllParameter();
    const printConfigResponse$ = this.gbPrintService.getPrintOptions();

    forkJoin([imageConfig$, formatConfig$, settingsConfig$, erweiterungConfig$, parameterConfig$, menuConfig$, printConfigResponse$]).subscribe({
      next: (results) => {
        this._formatConfig.next(new AppFormatConfig(<FormatConfigResponse>results[1]));
        this._settingsConfig.next(<SettingsConfigResponse>results[2]);
        this._erweiterungConfig.next(<ErweiterungConfigResponse>results[3]);
        this._parameterConfig.next(<ParameterConfigResponse>results[4]);
        this._imageConfigSubject.next(new AppImageConfig(<ImageConfigResponse>results[0], this.translateService));
        this._menuConfigSubject.next(<MenuConfigResponse>results[5]);
        const cookiePrintConfig = new CookiePrintConfig(<PrintConfigResponseMeta>results[6], this.appAuszugCookieService);
        cookiePrintConfig.init().then(() => {
          this._printConfig.next(cookiePrintConfig);
        });
        this._initializedSubject.next(true);
        resolve();
      }, error: () => resolve(false)
    });
  }

  private getDeploymentMode() {
    return this._deploymentMode === 'PUBLIC' ? 'PUBLIC' : 'MENU';
  }

  get menuConfig$(): Observable<MenuConfigResponse> {
    return this._menuConfig$.pipe(filter(menuConfig => menuConfig && !!menuConfig.entries));
  }

  get imageConfig$(): Observable<AppImageConfig> {
    return this._imageConfig$.pipe(filter(imageConfig => !!imageConfig));
  }

  get initialized$(): Observable<boolean> {
    return this._initialized$.pipe(filter(initialized => !!initialized));
  }

  get settingsConfig$(): Observable<SettingsConfigResponse> {
    return this._settingsConfig.asObservable().pipe(filter(settingsConfig => !!settingsConfig));
  }

  get erweiterungsConfig$(): Observable<ErweiterungConfigResponse> {
    return this._erweiterungConfig.asObservable().pipe(filter(erweiterungsConfig => erweiterungsConfig && !!erweiterungsConfig.activeErweiterungen));
  }

  get parameterConfig$(): Observable<ParameterConfigResponse> {
    return this._parameterConfig.asObservable().pipe(filter(parameterConfig => !!parameterConfig));
  }

  get formatConfig$(): Observable<AppFormatConfig> {
    return this._formatConfig.asObservable().pipe(filter(formatConfig => !!formatConfig));
  }

  get printConfig$(): Observable<AppPrintConfig> {
    return this._printConfig.asObservable().pipe(filter(printConfig => !!printConfig));
  }


}
